import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft } from "lucide-react";
import {
  LoadingState,
  ErrorState,
  EmptyState,
  StrengthBar,
  AttentionBadge,
  RecencyBadge,
  AskAboutThis,
  beliefSentence,
  directionInfo,
  confidenceWord,
  disagreementKind,
  humanise,
  recencyLine,
} from "@/components/research-hub";
import { useAnalystDossier } from "@/hooks/useResearchHubAnalyst";

type Row = Record<string, any>;

function provenanceKind(k: string | undefined): string {
  switch ((k ?? "").toLowerCase()) {
    case "from_documents":
    case "documents":
      return "from documents";
    case "inferred":
      return "inferred";
    case "manual":
    case "hand":
      return "entered by hand";
    default:
      return humanise(k) || "mixed";
  }
}

export default function ThesisDetail() {
  const { edgeId } = useParams<{ edgeId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useAnalystDossier(edgeId);

  if (isLoading) return <LoadingState rows={4} />;
  if (error) return <ErrorState onRetry={() => refetch()} />;
  if (!data || data.error === "not_a_thesis")
    return <EmptyState title="That item isn't a view we can deep-dive." />;

  const t: Row = data.thesis ?? {};
  const dir = directionInfo(t.thesis_direction ?? t.direction);
  const sentence = beliefSentence(t.from_label, t.to_label, t.thesis_direction ?? t.direction);
  // The dossier RPC can return a `{ error }` object in these slots instead of an array — guard so a
  // non-array never reaches .length/.map/.sort.
  const forEv: Row[] = Array.isArray(data.for_evidence) ? data.for_evidence : [];
  const againstEv: Row[] = Array.isArray(data.against_evidence) ? data.against_evidence : [];
  const evolution: Row[] = Array.isArray(data.evolution) ? data.evolution : [];
  const prov: Row = data.provenance ?? {};

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 font-body text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <Card className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-heading text-xl font-semibold">
            {dir.word && <span className="mr-1">{dir.word[0].toUpperCase() + dir.word.slice(1)}:</span>}
            {t.from_label && t.to_label ? `${t.from_label} → ${t.to_label}` : sentence}
          </h1>
          <AskAboutThis prefill={`Tell me more about our view that ${sentence}.`} />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StrengthBar value={t.weight ?? t.confidence} label={confidenceWord(t.thesis_confidence ?? t.confidence ?? t.weight)} />
          {(t.asserted_at || t.occurred_at) && <RecencyBadge date={t.asserted_at ?? t.occurred_at} />}
        </div>
      </Card>

      {/* The case for */}
      <section className="space-y-2">
        <h2 className="font-heading text-lg font-medium">The case for</h2>
        {forEv.length === 0 ? (
          <EmptyState title="No supporting evidence on file." />
        ) : (
          <Accordion type="multiple" className="space-y-2">
            {forEv.map((e: Row, i: number) => (
              <AccordionItem key={i} value={`for-${i}`} className="border rounded-lg px-3">
                <AccordionTrigger className="font-body text-sm hover:no-underline">
                  {beliefSentence(e.from_label, e.to_label, e.direction)}
                </AccordionTrigger>
                <AccordionContent className="font-body text-sm text-muted-foreground space-y-1">
                  <div>{humanise(e.edge_type) || "Supports this view"}</div>
                  <div className="flex items-center gap-3 text-xs">
                    {e.support_count != null && <span>{e.support_count} source{e.support_count === 1 ? "" : "s"}</span>}
                    {(e.occurred_at || e.asserted_at) && <RecencyBadge date={e.occurred_at ?? e.asserted_at} />}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </section>

      {/* The case against (the steelman) */}
      <section className="space-y-2">
        <h2 className="font-heading text-lg font-medium">The case against</h2>
        {againstEv.length === 0 ? (
          <EmptyState title="No counter-evidence on file." tone="good" />
        ) : (
          <div className="space-y-2">
            {[...againstEv]
              .sort((a: Row, b: Row) => (b.strength ?? 0) - (a.strength ?? 0))
              .map((e: Row, i: number) => (
                <Card key={i} className="p-3 space-y-2">
                  <AttentionBadge label={disagreementKind(e.disagreement_kind)} tone="bad" />
                  {e.evidence_quote && <p className="font-body text-sm italic text-foreground/90">"{e.evidence_quote}"</p>}
                  <div className="flex items-center gap-3 flex-wrap">
                    <StrengthBar value={e.strength} label="how strong" />
                    {e.source_count != null && (
                      <span className="font-body text-xs text-muted-foreground">
                        {e.source_count} source{e.source_count === 1 ? "" : "s"}
                      </span>
                    )}
                    {e.occurred_at && <RecencyBadge date={e.occurred_at} />}
                  </div>
                </Card>
              ))}
          </div>
        )}
      </section>

      {/* How this view moved */}
      {evolution.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-heading text-lg font-medium">How this view has moved</h2>
          <div className="border-l-2 border-border pl-4 space-y-3">
            {evolution.map((p: Row, i: number) => {
              const d = directionInfo(p.thesis_direction ?? p.direction);
              return (
                <div key={i} className="relative">
                  <span className="absolute -left-[1.3rem] top-1.5 h-2 w-2 rounded-full bg-primary" aria-hidden />
                  <div className="font-body text-sm">
                    {d.word ? `${d.word[0].toUpperCase()}${d.word.slice(1)}` : "View"} · {confidenceWord(p.thesis_confidence ?? p.confidence)}
                  </div>
                  <div className="font-body text-xs text-muted-foreground">{recencyLine(p.asserted_at ?? p.occurred_at)}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Trustworthiness of the sources */}
      <section className="space-y-2">
        <h2 className="font-heading text-lg font-medium">How trustworthy the sources are</h2>
        <Card className="p-4 space-y-2">
          <div className="font-body text-sm">
            {prov.n_distinct_sources != null ? `${prov.n_distinct_sources} distinct source${prov.n_distinct_sources === 1 ? "" : "s"}` : "Sources"}
            {" · "}
            {provenanceKind(prov.provenance_kind)}
          </div>
          {Array.isArray(prov.source_titles) && prov.source_titles.length > 0 && (
            <ul className="space-y-1">
              {prov.source_titles.map((srcRaw: any, i: number) => {
                const title = typeof srcRaw === "string" ? srcRaw : srcRaw?.title ?? "Source";
                const date = typeof srcRaw === "string" ? null : srcRaw?.date ?? srcRaw?.occurred_at;
                return (
                  <li key={i} className="flex items-center justify-between gap-2 font-body text-xs">
                    <span className="truncate">{title}</span>
                    {date && <RecencyBadge date={date} withIcon={false} />}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        <p className="font-body text-[11px] text-muted-foreground">Opening this view logs a research check.</p>
      </section>
    </div>
  );
}
